import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { can } from "@devengo/shared";

import { Button, Card, ErrorBox, PageTitle, tableClass, tdClass, thClass } from "../components/ui";
import { api } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { useBranding, useCurrentUser } from "../lib/session";

interface Grant {
  id: string;
  clientName: string;
  createdAt: string;
  lastUsedAt: string | null;
  refreshExpiresAt: string;
}

/** The current user's connected AI assistants (spec 013 US1/US5). */
export function AssistantsPage() {
  const user = useCurrentUser();
  if (!can(user.role, "connectAssistant")) {
    return (
      <>
        <PageTitle>Asistentes conectados</PageTitle>
        <Card>
          <p className="text-sm text-brand-muted">
            Los asistentes de IA los conecta solo el Dueño del estudio.
          </p>
        </Card>
      </>
    );
  }
  return <Assistants />;
}

function Assistants() {
  const branding = useBranding();
  const client = useQueryClient();
  const grants = useQuery({
    queryKey: ["oauth-grants"],
    queryFn: () => api.get<Grant[]>("/api/oauth/grants"),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/api/oauth/grants/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ["oauth-grants"] }),
  });
  const endpoint = `${window.location.origin}/mcp`;
  return (
    <>
      <PageTitle subtitle="Claude, ChatGPT u otro asistente que actúa en el sistema como vos, con tus permisos.">
        Asistentes conectados
      </PageTitle>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title="Mis conexiones" className="lg:col-span-2">
          <ErrorBox error={grants.error ?? revoke.error} />
          {grants.data?.length ? (
            <table className={tableClass}>
              <thead>
                <tr>
                  <th className={thClass}>Asistente</th>
                  <th className={thClass}>Conectado</th>
                  <th className={thClass}>Último uso</th>
                  <th className={thClass} />
                </tr>
              </thead>
              <tbody>
                {grants.data.map((g) => (
                  <tr key={g.id}>
                    <td className={tdClass}>{g.clientName}</td>
                    <td className={`${tdClass} tabular`}>{formatDateTime(g.createdAt)}</td>
                    <td className={`${tdClass} tabular`}>
                      {g.lastUsedAt ? formatDateTime(g.lastUsedAt) : "—"}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      <Button
                        variant="secondary"
                        disabled={revoke.isPending}
                        onClick={() => revoke.mutate(g.id)}
                      >
                        Desconectar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-brand-muted">No tenés ningún asistente conectado.</p>
          )}
        </Card>
        <Card title="Cómo conectar uno">
          <ol className="list-decimal space-y-2 pl-5 text-sm">
            <li>
              En Claude: Configuración → Conectores → Agregar conector personalizado. En ChatGPT:
              Configuración → Conectores (modo desarrollador).
            </li>
            <li>
              Pegá esta dirección:
              <code className="mt-1 block break-all rounded bg-brand-tint px-2 py-1 text-xs">
                {endpoint}
              </code>
            </li>
            <li>Ingresá con tu usuario de {branding.displayName} y tocá «Permitir».</li>
          </ol>
        </Card>
      </div>
    </>
  );
}
